import React, { FC, useCallback } from "react";
import { SUPPORTED_WALLETS, injected } from "../../config/wallets";

import { AppDispatch } from "../../state";
import Button from "../Button";
import Image from "next/image";
import ModalHeader from "../ModalHeader";
import Transaction from "./Transaction";
import Typography from "../Typography";
import { shortenAddress } from "../../functions";
import { t } from "@lingui/macro";
import { useActiveWeb3React } from "../../hooks/useActiveWeb3React";
import { useDispatch } from "react-redux";
import { useLingui } from "@lingui/react";
import { clearAllTransactions } from "../../state/transactions/actions";
import { isMobile } from "react-device-detect";
import { WalletConnectConnector } from "@web3-react/walletconnect-connector";
import { CopyIcon } from "../Icons/Copy";
import { ToastType, useToasts } from "../../shared/providers/toast";
const WalletIcon: FC<{ size?: number; src: string; alt: string }> = ({
  size,
  src,
  alt,
  children,
}) => {
  return (
    <div className="flex flex-row items-end justify-center mr-2 flex-nowrap lg:items-center">
      <Image src={src} alt={alt} width={size} height={size} />
      {children}
    </div>
  );
};

function renderTransactions(transactions: string[]) {
  return (
    <div className="flex flex-col flex-nowrap zText-1">
      {transactions.map((hash, i) => {
        return <Transaction key={i} hash={hash} />;
      })}
    </div>
  );
}

interface AccountDetailsProps {
  toggleWalletModal: () => void;
  pendingTransactions: string[];
  confirmedTransactions: string[];
  ENSName?: string;
  openOptions: () => void;
}

const AccountDetails: FC<AccountDetailsProps> = ({
  toggleWalletModal,
  pendingTransactions,
  confirmedTransactions,
  ENSName,
  openOptions,
}) => {
  const { i18n } = useLingui();
  const { chainId, account, connector, deactivate } = useActiveWeb3React();
  const dispatch = useDispatch<AppDispatch>();
  const { show } = useToasts();
  function formatConnectorName() {
    const { ethereum } = window;
    const isMetaMask = !!(ethereum && ethereum.isMetaMask);
    const name = Object.keys(SUPPORTED_WALLETS)
      .filter((k) => {
        return (
          SUPPORTED_WALLETS[k].connector === connector &&
          (connector !== injected || isMetaMask === (k === "METAMASK"))
        );
      })
      .map((k) => SUPPORTED_WALLETS[k].name)[0];

    return (
      <div className="font-bold text-[16px]  zText-1">
        Connected with {name}
      </div>
    );
  }

  function getStatusIcon() {
    if (connector === injected) {
      return null;
      // return <IconWrapper size={16}>{/* <Identicon /> */}</IconWrapper>
    } else if (connector.constructor.name === "WalletConnectConnector") {
      return (
        <WalletIcon
          src="/images/wallets/wallet-connect.svg"
          alt="Wallet Connect"
          size={16}
        />
      );
    } else if (connector.constructor.name === "WalletLinkConnector") {
      return (
        <WalletIcon
          src="/images/wallets/coinbase.svg"
          alt="Coinbase"
          size={16}
        />
      );
    } else if (connector.constructor.name === "PortisConnector") {
      return (
        <WalletIcon src="/images/wallets/portis.png" alt="Portis" size={16}>
          <Button
            onClick={async () => {
              // casting as PortisConnector here defeats the lazyload purpose
              (connector as any).portis.showPortis();
            }}
          >
            Show Portis
          </Button>
        </WalletIcon>
      );
    }
    return null;
  }

  const clearAllTransactionsCallback = useCallback(() => {
    if (chainId) dispatch(clearAllTransactions({ chainId }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chainId]);

  const hasTransactions =
    !!pendingTransactions.length || !!confirmedTransactions.length;

  const handleDisconnect = () => {
    if (connector !== injected) {
      if (connector instanceof WalletConnectConnector) {
        deactivate();
        localStorage.removeItem("walletconnect");
        connector.walletConnectProvider = undefined;
      }
      (connector as any).close();
    } else {
      deactivate();
    }
  };
  const handleCopyContract = (value) => {
    if (value) {
      navigator.clipboard.writeText(value)
      show("Copied!", ToastType?.SUCCESS)
    }
  }
  return (
    <div className="space-y-2">
      <div className="space-y-3">
        <ModalHeader
          title="Account"
          onClose={toggleWalletModal}
          className="zText-1"
          isShowBackBtn={false}
        />
        <div className="space-y-3 text-[16px]">
          <div className="flex items-center justify-between">
            {formatConnectorName()}
            <div className="flex space-x-3 place-items-center">
              {/* {(connector !== injected || !isMobile) && (
                <Button
                  variant="outlined"
                  color="red"
                  size="none"
                  onClick={handleDisconnect}
                  className="text-white font-normal rounded-full bg-red-btn text-[16px] px-1"
                >
                  {i18n._(t`Disconnect`)}
                </Button>
              )} */}
              {!isMobile && (
                <Button
                  variant="outlined"
                  color="red"
                  size="xs"
                  onClick={() => {
                    openOptions();
                  }}
                  className="text-white font-normal rounded-full bg-red-btn text-[14px] px-3"
                >
                  {i18n._(t`Change`)}
                </Button>
              )}
            </div>
          </div>
          <div
            id="web3-account-identifier-row"
            className="flex flex-col justify-center space-y-3"
          >
            {ENSName ? (
              <div className="bg-dark-8 zText-1">
                {getStatusIcon()}
                <Typography>{ENSName}</Typography>
              </div>
            ) : (
              <div className="px-3 py-2 rounded zBg-3 zBorder-light zText-1">
                {getStatusIcon()}
                <Typography>
                  {
                    account && (
                      <div className="relative cursor-pointer" onClick={(e)=>handleCopyContract(account)}>
                        <div className="flex">
                          <span className="mr-3">{shortenAddress(account)}</span>
                          <span className="w-6 h-6 text-red">
                            <CopyIcon/>
                          </span>
                        </div>
                        
                        <div className="absolute right-0 top-0 flex">
                          {(connector !== injected || !isMobile) && (
                            <Button
                              variant="outlined"
                              color="default"
                              size="none"
                              onClick={handleDisconnect}
                              className="text-white font-normal rounded-full text-[14px] px-3 ml-3 border border-red-500 cursor-pointer"
                            >
                              {i18n._(t`Disconnect`)}
                            </Button>
                          )}
                        </div>
                      </div>
                    )
                  }
                </Typography>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="space-y-4 pt-4">
        <div className="flex items-center justify-between zText-1">
          <Typography weight={600}>{i18n._(t`Recent Transactions`)}</Typography>
          {hasTransactions && (
            <div>
              <Button
                variant="outlined"
                color="default"
                size="xs"
                onClick={clearAllTransactionsCallback}
                className="zText-4 font-normal rounded-full text-[14px] px-1"
              >
                {i18n._(t`Clear all`)}
              </Button>
            </div>
          )}
        </div>
        {hasTransactions ? (
          <div className="max-h-160 flex flex-col overflow-y-auto zText-1">
            {renderTransactions(pendingTransactions)}
            {renderTransactions(confirmedTransactions)}
          </div>
        ) : (
          <Typography variant="sm" className="text-secondary">
            {i18n._(t`Your transactions will appear here...`)}
          </Typography>
        )}
      </div>
    </div>
  );
};

export default AccountDetails;
